'use strict';

var _ = require('lodash');
var ManagedEntitiesService = function ManagedEntitiesService($q, restangular, ManagedMarket, ManagedLeg, ScheduleChangeConstants, ConfigurationService, hepService, featureToggleService) {
  /**
   *
   * Create array of objects
   * @param {Function} Factory
   * @param {Array} entities
   * @param {Array} data
   * @param {Array} data.market
   * @return {Array}
   */
  function createObjectsArray(Factory, entities, data) {
    return _.map(entities, function (entity) {
      var entitySplit = _.trim(entity).split('-');
      var managedEntity = _.find(data, {
        origin: entitySplit[0],
        destination: entitySplit[1]
      });
      if (_.isEmpty(managedEntity)) {
        managedEntity = {
          isNew: true,
          origin: entitySplit[0],
          destination: entitySplit[1],
          isSignificant: featureToggleService.isDisabled('SML') || featureToggleService.isEnabled('BREAK_CONNECTIONS'),
          isNotSaved: true
        };
      }
      return new Factory(managedEntity);
    });
  }
  function getMarketEntities(entities) {
    return _.filter(ConfigurationService.getMarkets(), function (m) {
      return _.includes(entities, m.origin + '-' + m.destination);
    });
  }
  function marketKey(origin, destination, spos) {
    return origin + '-' + destination + ' (' + spos + ')';
  }
  function loadMarketData(marketList) {
    var query = _.chain(marketList).flatMap(function (m) {
      return _.map(m.significantPos, function (p) {
        return marketKey(m.origin, m.destination, p.spos);
      });
    }).join(';').value();
    return restangular.one('schedulechange').one('market').one('load').get({
      markets: query
    });
  }

  /**
   * Fetch markets data
   * @param {Array} entities
   */
  function prepareMarkets(entities) {
    var markets = _.cloneDeep(getMarketEntities(entities));
    var marketList = createObjectsArray(ManagedMarket, entities, updateMarketsPoses(markets));
    return loadMarketData(marketList).then(function (data) {
      var marketMap = _.keyBy(data.managedMarkets, function (m) {
        return marketKey(m.market.origin, m.market.destination, m.market.spos);
      });
      _.forEach(marketList, function (m) {
        _.forEach(m.significantPos, function (p) {
          var managedMarket = marketMap[marketKey(m.origin, m.destination, p.spos)];
          var sponsor = managedMarket.sponsor;
          m.assignedEntity = managedMarket.entityAssignment;
          if (sponsor) {
            var sponsorKey = marketKey(sponsor.origin, sponsor.destination, sponsor.spos);
            p.sponsor = _.extend({
              id: sponsorKey
            }, sponsor);
          }
        });
      });
      return marketList;
    });
  }
  function updateMarketsPoses(markets) {
    var poses = ConfigurationService.getPoses();
    _.forEach(markets, function (market) {
      market.pos = _.filter(poses, function (pos) {
        return pos.origin === market.origin && pos.destination === market.destination;
      });
    });
    return markets;
  }

  /**
   * Updates managedLegs with leg sponsors if they are specified.
   * @param managedLegs
   * @returns {*}
   */
  function fillLegSponsors(managedLegs) {
    // for each leg get show up init profile for given (origin, destination)
    var promises = _.chain(managedLegs).filter({
      isNew: false
    }).map(function (leg) {
      return hepService.getShowUpInitProfiles(leg.origin, leg.destination);
    }).value();
    return $q.all(promises).then(function (legResults) {
      legResults.forEach(function (profile) {
        // Schedule Change does not allow to specify sponsor per cabin so we can read
        // the first one that is not for the flight level
        var hepProfile = _.head(_.filter(profile.hepList, function (d) {
          return _.isUndefined(d.attributes.legBusinessId);
        }));
        if (hepProfile) {
          var sponsor = hepProfile.properties.profile.sponsor;
          if (sponsor !== undefined) {
            var managedLeg = _.find(managedLegs, {
              origin: hepProfile.attributes.legOrigin,
              destination: hepProfile.attributes.legDestination
            });
            if (managedLeg !== undefined) {
              var split = sponsor.split('-');
              managedLeg.sponsor = {
                id: sponsor,
                origin: split[0],
                destination: split[1]
              };
            }
          }
        }
      });
      return managedLegs;
    });
  }

  /**
   * Updates managedLegs with flight sponsors if they are specified.
   * @param managedLegs
   * @returns {*}
   */
  function fillFlightSponsors(managedLegs) {
    // for each leg get show up init profile for given (origin, destination)
    var promises = _.chain(managedLegs).filter({
      isNew: false
    }).map(function (leg) {
      return hepService.getShowUpInitProfiles(leg.origin, leg.destination);
    }).value();
    return $q.all(promises).then(function (legResults) {
      legResults.forEach(function (hepData) {
        var firstHepProfile = _.head(hepData.hepList);
        var flightSponsors = _.chain(hepData.hepList).filter(function (p) {
          return p.attributes.legBusinessId !== undefined && p.properties.profile.sponsor !== undefined;
        }).uniqBy('attributes.legBusinessId').map(function (p) {
          var split = p.properties.profile.sponsor.split('-');
          var legBusinessId = p.attributes.legBusinessId.split('#').pop();
          return {
            flightSummary: '',
            sourceBusinessId: legBusinessId,
            businessId: split[2],
            selected: {
              origin: split[0],
              destination: split[1]
            }
          };
        }).value();
        if (firstHepProfile && firstHepProfile.attributes) {
          var managedLeg = _.find(managedLegs, {
            origin: firstHepProfile.attributes.legOrigin,
            destination: firstHepProfile.attributes.legDestination
          });
          if (managedLeg !== undefined) {
            managedLeg.flightSponsor = flightSponsors;
          }
        }
      });
      return managedLegs;
    });
  }

  /**
   * Fetch legs data
   * @param {Array} entities
   */
  function prepareLegs(entities) {
    return restangular.one('staticdata').one('legs').get().then(function (data) {
      var managedLegs = createObjectsArray(ManagedLeg, entities, data.leg);
      return fillLegSponsors(managedLegs).then(fillFlightSponsors);
    });
  }

  /**
   *
   * @param {(string|string[])} entities
   * @returns {*}
   */
  function unifyEntities(entities) {
    if (_.isArray(entities)) {
      return entities;
    } else {
      return _.chain(entities.split(',')).map(_.trim).uniq().value();
    }
  }

  /**
   * Update markets data
   * @param {Object} market
   */
  function updateMarket(market) {
    return restangular.one('staticdata').one('markets').customPUT(market);
  }

  /**
   * Update markets entity data
   * @param {Object} marketEntityHepEntries
   */
  function updateMarketEntity(marketEntityHepEntries) {
    return restangular.service('configuration').one('hep').one('bulk').customPUT({
      value: marketEntityHepEntries
    }, undefined, {
      upsert: true
    });
  }

  /**
   * Update legs data
   * @param {Object} leg
   */
  function updateLegs(leg) {
    return restangular.one('staticdata').one('legs').customPUT(leg);
  }

  /**
   * Insert legs data
   * @param {Object} leg
   */
  function insertLegs(leg) {
    return restangular.one('staticdata').one('legs').patch(leg);
  }

  /**
   * Update spos data
   * @param {Object} poses
   */
  function updatePoses(poses) {
    return restangular.one('staticdata').one('poses').customPUT(poses);
  }

  /**
   * Update Sponsors data
   * @param {Array} hep
   */
  function addSponsorHep(hep) {
    return hepService.addHep(hep);
  }

  /**
   * Update Host Share data
   * @param {Array} hostshare
   */
  function updateHostShare(hostshare) {
    return hepService.updateHostShare(hostshare);
  }

  /**
   +     * Update FlightEntity data
   +     * @param {Array} flightEntity
   +     */
  function addFlightEntityHep(flightEntity) {
    return hepService.addHep(flightEntity);
  }

  /**
   *
   * @param {string} type
   * @param {(string|string[])} entities
   * @returns {*}
   */
  function prepareData(type, entities) {
    var entitiesArray = unifyEntities(entities);
    switch (type) {
      case ScheduleChangeConstants.ENTITIES.MARKETS:
        return prepareMarkets(entitiesArray);
      case ScheduleChangeConstants.ENTITIES.LEGS:
      case ScheduleChangeConstants.ENTITIES.FLIGHTS:
        return prepareLegs(entitiesArray);
    }
  }
  return {
    prepareData: prepareData,
    updateMarket: updateMarket,
    updateMarketEntity: updateMarketEntity,
    updateLegs: updateLegs,
    insertLegs: insertLegs,
    updatePoses: updatePoses,
    addSponsorHep: addSponsorHep,
    addFlightEntityHep: addFlightEntityHep,
    updateHostShare: updateHostShare
  };
};
ManagedEntitiesService.$inject = ['$q', 'Restangular', 'ManagedMarketFactory', 'ManagedLegFactory', 'ScheduleChangeConstants', 'ConfigurationService', 'HepService', 'FeatureToggleService'];
module.exports = ManagedEntitiesService;